import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { ErrorToastBlacklistService } from '@lsq-frontend/angular-shared/features/error-toast-blacklist-service';
import { getRouterLinkUrlById, ignoreNullish } from '@lsq-frontend/common-ui';
import { ADMIN_SUPPLIERS, atlas } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    GetSupplierDetailsForOperationsRequest,
    GetSupplierDetailsForOperationsResponse,
    GetSupplierDetailsForOperationsService,
    SendSupplierEnrollmentLinkRequest,
    SetFeeStructureRequest,
    SetFeeStructureService,
    SetSupplierPrimaryContactRequest,
    SetSupplierPrimaryContactService,
    UpdateSupplierPrimaryContactRequest,
    UpdateSupplierPrimaryContactService
} from '@lsq-frontend/remote-services/all-java/financing-mk1-services';
import {
    GetSupplierRelationshipDetailsRequest,
    GetSupplierRelationshipDetailsResponse,
    GetSupplierRelationshipDetailsService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-supplier/get-supplier-relationship-details';
import {
    AdminManageSupplierSetFeeStructureRequest,
    SetFeeStructureService as SetFeeStructureServiceMk2
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-supplier/set-fee-structure';
import { Guid, ResponseCode } from '@lsq-frontend/remote-services/enterprise/common';
import { BehaviorSubject } from 'rxjs';
import { first, pluck } from 'rxjs/operators';
import { ISettingsUiControl } from '../../models';
import { HttpClient } from '@angular/common/http';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';

@Injectable({ providedIn: 'root' })
export class ManageSupplierService {
    public defaultSettings: ISettingsUiControl = {};
    public currentSettings: ISettingsUiControl = {};
    public currentOverridingSettings: ISettingsUiControl = {};

    private _currentSupplierRelationshipSubject =
        new BehaviorSubject<GetSupplierRelationshipDetailsResponse>(null);
    public currentSupplierRelationship$ = this._currentSupplierRelationshipSubject.asObservable();
    public relationshipKey$ = this.currentSupplierRelationship$.pipe(
        ignoreNullish(),
        pluck('relationshipKey')
    );
    private _supplierDetailsSubject = new BehaviorSubject<GetSupplierDetailsForOperationsResponse>(
        null
    );
    public supplierDetails$ = this._supplierDetailsSubject.asObservable();
    constructor(
        private _getSupplierRelationshipDetailsService: GetSupplierRelationshipDetailsService,
        private _getSupplierDetailsForOperationsService: GetSupplierDetailsForOperationsService,
        private _setSupplierPrimaryContactService: SetSupplierPrimaryContactService,
        private _updateSupplierPrimaryContactService: UpdateSupplierPrimaryContactService,
        private _adminManageSupplierSetFeeStructureService: SetFeeStructureServiceMk2,
        private _setFeeStructureRequestService: SetFeeStructureService,
        private _router: Router,
        private _alertService: AlertService,
        private _errorToastBlacklistService: ErrorToastBlacklistService,
        private _http: HttpClient,
        @Inject(SETTINGS) private _settings: ISettings
    ) {
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            'operations/AdminManageSupplier/GetSupplierRelationshipDetails',
            ResponseCode.Error
        );
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            'operations/GetSupplierDetailsForOperations',
            ResponseCode.Error
        );
    }

    public async loadRelationship(relationshipKey: Guid): Promise<void> {
        try {
            this._currentSupplierRelationshipSubject.next(null);
            this._currentSupplierRelationshipSubject.next(
                await this._getSupplierRelationshipDetailsService.executeOnce(
                    new GetSupplierRelationshipDetailsRequest({
                        relationshipKey
                    })
                )
            );
        } catch {
            this._handleLoadSupplierError();
        }
    }

    public async loadSupplierDetails(supplierKey: Guid): Promise<void> {
        try {
            this._supplierDetailsSubject.next(null);
            this._supplierDetailsSubject.next(
                await this._getSupplierDetailsForOperationsService.executeOnce(
                    new GetSupplierDetailsForOperationsRequest({
                        supplierKey
                    })
                )
            );
        } catch {
            this._handleLoadSupplierError();
        }
    }

    public async setSupplierPrimaryContactService(
        request: SetSupplierPrimaryContactRequest
    ): Promise<void> {
        await this._setSupplierPrimaryContactService.executeOnce(request);
    }
    public async updateSupplierPrimaryContactService(
        request: UpdateSupplierPrimaryContactRequest
    ): Promise<void> {
        await this._updateSupplierPrimaryContactService.executeOnce(request);
    }

    public async sendEnrollmentEmail(
        relationshipKey: string,
        notificationEmails: string[]
    ): Promise<void> {
        await this._http
            .post(
                `${this._settings.httpApiServiceNameUrlMap['supplierMgmtApi']}/v1/scf/sendEnrollmentLink`,
                {
                    id: relationshipKey,
                    notificationEmails
                }
            )
            .pipe(first())
            .toPromise();
    }

    public async saveFeeStructureLegacy(request: SetFeeStructureRequest): Promise<void> {
        await this._setFeeStructureRequestService.executeOnce(request);
    }

    public async saveFeeStructure(
        request: AdminManageSupplierSetFeeStructureRequest
    ): Promise<void> {
        await this._adminManageSupplierSetFeeStructureService.executeOnce(request);
    }

    private _handleLoadSupplierError(): void {
        this._router.navigateByUrl(getRouterLinkUrlById(atlas, ADMIN_SUPPLIERS));
        this._alertService.error('Could not load supplier with provided key.');
    }
}
